import { World, type Body, type WorldSnapshot } from "fast-2d";
import { checksumOf, diffWorldState, type WorldDiff } from "fast-2d/devtools";
import { Camera } from "../render/Camera";
import type { CanvasRenderer } from "../render/CanvasRenderer";
import type { Demo, Scene, SceneAction } from "./types";
import { ball, box, rng } from "./helpers";

const FIXED_DT = 1 / 60;
const MAX_CATCHUP_STEPS = 5;
const CHECKPOINT_INTERVAL = 30;
const SCENE_SEED = 0xf2d2026;
const INPUT_SEED = 0x51f15eED;

interface Client {
	world: World;
	camera: Camera;
	dynamicBodies: Body[];
}

interface LocalizedDiff {
	index: number;
	field: string;
	a: number | boolean | null;
	b: number | boolean | null;
}

interface LockstepState {
	tick: number;
	checksumA: number;
	checksumB: number;
	desyncTick: number | null;
	diff: WorldDiff | null;
	localizedDiff: LocalizedDiff;
	checkpointTick: number;
	checkpointB: WorldSnapshot | null;
	lastReplaySteps: number;
}

export const lockstep: Demo = {
	id: "lockstep",
	title: "Lockstep multiplayer",
	blurb:
		"Two clients. One deterministic world. Provably in sync — and when they drift, the checksum catches it instantly.",
	category: "Featured",
	build() {
		let clientA = buildClient();
		let clientB = buildClient();
		const stageCamera = new Camera(0, -3, 36);
		let accumulator = 0;
		let scene: Scene;

		const state: LockstepState = {
			tick: 0,
			checksumA: checksumOf(clientA.world),
			checksumB: checksumOf(clientB.world),
			desyncTick: null,
			diff: null,
			localizedDiff: { index: -1, field: "", a: null, b: null },
			checkpointTick: 0,
			checkpointB: null,
			lastReplaySteps: 0,
		};

		const captureCheckpoint = (): void => {
			state.checkpointTick = state.tick;
			state.checkpointB = clientB.world.snapshot(state.checkpointB ?? undefined);
		};

		const shouldCaptureCheckpoint = (): boolean =>
			state.desyncTick === null && state.tick - state.checkpointTick >= CHECKPOINT_INTERVAL;

		const refreshChecksums = (): void => {
			state.checksumA = checksumOf(clientA.world);
			state.checksumB = checksumOf(clientB.world);
			state.diff = diffWorldState(clientA.world, clientB.world, state.diff ?? undefined, {
				fields: state.checksumA === state.checksumB ? "checksum" : "dynamic",
			});
			if (state.checksumA === state.checksumB) {
				state.desyncTick = null;
				state.localizedDiff.index = -1;
				return;
			}
			if (state.desyncTick === null) state.desyncTick = state.tick;
			localizeBodyDiff(clientA, clientB, state.localizedDiff);
		};

		const tickOnce = (): void => {
			applySharedInput(clientA, state.tick);
			applySharedInput(clientB, state.tick);
			clientA.world.update(FIXED_DT);
			clientB.world.update(FIXED_DT);
			state.tick++;
			refreshChecksums();
			if (shouldCaptureCheckpoint()) captureCheckpoint();
		};

		const injectDesync = (): void => {
			if (state.desyncTick !== null) return;
			captureCheckpoint();
			const bodies = clientB.dynamicBodies;
			const body = bodies[(state.tick * 7 + 3) % bodies.length]!;
			body.translate([0.045, -0.025]);
			body.applyImpulse([0.42, -0.31], [body.position.x + 0.2, body.position.y - 0.1]);
			state.lastReplaySteps = 0;
			refreshChecksums();
		};

		const resync = (): void => {
			const targetTick = state.tick;
			const rollbackTick = state.checkpointTick;
			const checkpoint = state.checkpointB;
			if (checkpoint === null) return;

			clientB.world.restore(checkpoint);
			state.tick = rollbackTick;
			state.desyncTick = null;
			state.diff = null;
			for (let tick = rollbackTick; tick < targetTick; tick++) {
				applySharedInput(clientB, tick);
				clientB.world.update(FIXED_DT);
				state.tick++;
			}
			accumulator = 0;
			state.lastReplaySteps = targetTick - rollbackTick;
			refreshChecksums();
			if (state.desyncTick === null) captureCheckpoint();
		};

		captureCheckpoint();

		const actions: SceneAction[] = [
			{ label: "Inject divergence", run: injectDesync, variant: "danger" },
			{ label: "Resync rollback", run: resync, variant: "success" },
		];

		scene = {
			world: clientA.world,
			worlds: [clientA.world, clientB.world],
			camera: stageCamera,
			draggable: false,
			hint:
				"Two lockstep clients run the same deterministic inputs. Inject divergence to localize the first changed body field; Resync rolls Client B back to its last-good snapshot and replays shared inputs.",
			update(dt) {
				accumulator += Math.min(dt, 0.1);
				let steps = 0;
				while (accumulator >= FIXED_DT && steps < MAX_CATCHUP_STEPS) {
					tickOnce();
					accumulator -= FIXED_DT;
					steps++;
				}
				if (steps === MAX_CATCHUP_STEPS && accumulator > FIXED_DT) accumulator = 0;
				return steps;
			},
			render(renderer: CanvasRenderer) {
				renderLockstep(renderer, stageCamera, clientA, clientB, state);
			},
			actions: () => actions,
		};

		return scene;
	},
};

function buildClient(): Client {
	const world = new World({
		gravity: [0, 9.81],
		fixedTimeStep: FIXED_DT,
		maxSubSteps: 8,
		sleep: false,
		solver: {
			substeps: 8,
			velocityIterations: 12,
			positionIterations: 8,
			contactHertz: 60,
			contactDampingRatio: 8,
			maxContactPushSpeed: 30,
		},
	});
	const dynamicBodies: Body[] = [];
	const random = rng(SCENE_SEED);

	box(world, 0, 3.7, 16.4, 5.0, { isStatic: true, friction: 0.9 });
	box(world, -8.45, -3.8, 0.7, 10.7, { isStatic: true, friction: 0.7 });
	box(world, 8.45, -3.8, 0.7, 10.7, { isStatic: true, friction: 0.7 });
	box(world, -3.8, -2.05, 4.4, 0.24, { isStatic: true, angle: 0.2, friction: 0.75 });
	box(world, 3.7, -3.05, 4.5, 0.24, { isStatic: true, angle: -0.22, friction: 0.75 });
	box(world, -0.2, -4.7, 3.4, 0.22, { isStatic: true, angle: 0.13, friction: 0.75 });

	for (let i = 0; i < 30; i++) {
		const column = i % 10;
		const row = Math.floor(i / 10);
		const x = -5.4 + column * 1.2 + (random() - 0.5) * 0.18;
		const y = -8.2 + row * 0.72 + (random() - 0.5) * 0.16;
		if (i % 3 === 0) {
			dynamicBodies.push(
				box(world, x, y, 0.64, 0.64, {
					angle: (random() - 0.5) * 0.5,
					friction: 0.52,
					restitution: 0.24,
					density: 1.15,
				}),
			);
		} else {
			dynamicBodies.push(
				ball(world, x, y, 0.33 + random() * 0.08, {
					friction: 0.38,
					restitution: 0.42,
					density: 1.0 + random() * 0.35,
				}),
			);
		}
	}

	return { world, camera: new Camera(0, -3.35, 34), dynamicBodies };
}

function applySharedInput(client: Client, tick: number): void {
	if (tick % 14 !== 4) return;
	const h = hash32((tick + 1) ^ INPUT_SEED);
	const bodies = client.dynamicBodies;
	const body = bodies[h % bodies.length]!;
	const horizontal = (((h >>> 8) & 0xff) / 255 - 0.5) * 2.8;
	const lift = -1.8 - (((h >>> 16) & 0xff) / 255) * 2.4;
	const offset = (((h >>> 24) & 0xff) / 255 - 0.5) * 0.35;
	body.applyImpulse([horizontal, lift], [body.position.x + offset, body.position.y - 0.1]);
}

function hash32(value: number): number {
	let x = value >>> 0;
	x ^= x >>> 16;
	x = Math.imul(x, 0x7feb352d);
	x ^= x >>> 15;
	x = Math.imul(x, 0x846ca68b);
	x ^= x >>> 16;
	return x >>> 0;
}

function renderLockstep(
	renderer: CanvasRenderer,
	stageCamera: Camera,
	clientA: Client,
	clientB: Client,
	state: LockstepState,
): void {
	renderer.setStatusText(statusText(state));
	const width = stageCamera.screenW;
	const height = stageCamera.screenH;
	const compact = width < 720;
	const statusHeight = compact ? 112 : 78;
	const gutter = 10;
	const paneY = statusHeight;
	const paneH = Math.max(120, height - statusHeight);
	const paneW = Math.max(120, (width - gutter) / 2);
	const scale = Math.max(18, Math.min((paneW - 28) / 16.8, (paneH - 34) / 10.8));

	clientA.camera.centerX = 0;
	clientA.camera.centerY = -3.35;
	clientA.camera.scale = scale;
	clientB.camera.centerX = 0;
	clientB.camera.centerY = -3.35;
	clientB.camera.scale = scale;

	renderer.renderPanes(
		[
			{
				world: clientA.world,
				camera: clientA.camera,
				viewport: { x: 0, y: paneY, width: paneW, height: paneH },
				overlay: (ctx, cam) => {
					drawDiffHighlight(ctx, cam, clientA, state);
					drawClientLabel(ctx, "Client A", state.checksumA, true);
				},
				colorSeed: "order",
			},
			{
				world: clientB.world,
				camera: clientB.camera,
				viewport: { x: paneW + gutter, y: paneY, width: paneW, height: paneH },
				overlay: (ctx, cam) => {
					drawDiffHighlight(ctx, cam, clientB, state);
					drawClientLabel(ctx, "Client B", state.checksumB, state.desyncTick === null);
				},
				colorSeed: "order",
			},
		],
		(ctx) => drawStatus(ctx, width, height, paneW, gutter, state),
	);
}

function drawDiffHighlight(
	ctx: CanvasRenderingContext2D,
	cam: Camera,
	client: Client,
	state: LockstepState,
): void {
	const bodyIndex = state.localizedDiff.index;
	if (bodyIndex < 0) return;
	const body = client.dynamicBodies[bodyIndex];
	if (!body) return;
	body.synchronize();
	const x = cam.worldToScreenX(body.minX);
	const y = cam.worldToScreenY(body.minY);
	const w = (body.maxX - body.minX) * cam.scale;
	const h = (body.maxY - body.minY) * cam.scale;
	ctx.save();
	ctx.lineWidth = 3;
	ctx.strokeStyle = "rgba(255, 107, 107, 0.95)";
	ctx.fillStyle = "rgba(255, 107, 107, 0.1)";
	ctx.fillRect(x - 5, y - 5, w + 10, h + 10);
	ctx.strokeRect(x - 5, y - 5, w + 10, h + 10);
	ctx.restore();
}

function drawClientLabel(
	ctx: CanvasRenderingContext2D,
	label: string,
	checksum: number,
	inSync: boolean,
): void {
	ctx.save();
	roundRect(ctx, 12, 12, 178, 48, 8);
	ctx.fillStyle = "rgba(8, 10, 16, 0.76)";
	ctx.fill();
	ctx.strokeStyle = inSync ? "rgba(22, 240, 180, 0.35)" : "rgba(255, 107, 107, 0.55)";
	ctx.stroke();
	ctx.fillStyle = "#e7eaf3";
	ctx.font = "700 13px ui-sans-serif, system-ui";
	ctx.fillText(label, 24, 32);
	ctx.fillStyle = "#9aa3bd";
	ctx.font = "12px ui-monospace, SFMono-Regular, Consolas, monospace";
	ctx.fillText(formatHex(checksum), 24, 50);
	ctx.restore();
}

function drawStatus(
	ctx: CanvasRenderingContext2D,
	width: number,
	height: number,
	paneW: number,
	gutter: number,
	state: LockstepState,
): void {
	const synced = state.checksumA === state.checksumB;
	const compact = width < 720;
	const boxW = compact ? Math.max(280, width - 24) : Math.min(560, width - 330);
	const boxH = compact ? 72 : 68;
	const x = compact ? 12 : width - boxW - 12;
	const y = compact ? 50 : 12;

	ctx.save();
	roundRect(ctx, x, y, boxW, boxH, 10);
	ctx.fillStyle = synced ? "rgba(8, 48, 35, 0.9)" : "rgba(62, 18, 25, 0.93)";
	ctx.fill();
	ctx.strokeStyle = synced ? "rgba(22, 240, 180, 0.72)" : "rgba(255, 107, 107, 0.82)";
	ctx.stroke();

	ctx.fillStyle = synced ? "#c7fff0" : "#ffdada";
	ctx.font = "700 14px ui-sans-serif, system-ui";
	const headline = synced
		? `In sync: ${formatHex(state.checksumA)} = ${formatHex(state.checksumB)}`
		: `Desync at tick ${state.desyncTick ?? state.tick}`;
	ctx.fillText(headline, x + 14, y + 22);

	ctx.fillStyle = "rgba(231, 234, 243, 0.78)";
	ctx.font = "12px ui-monospace, SFMono-Regular, Consolas, monospace";
	const detail = synced
		? `tick ${state.tick} | replayed ${state.lastReplaySteps} steps`
		: `${formatHex(state.checksumA)} != ${formatHex(state.checksumB)}`;
	ctx.fillText(detail, x + 14, y + 42);
	if (!synced) {
		ctx.fillStyle = "#ffdada";
		ctx.font = "12px ui-sans-serif, system-ui";
		ctx.fillText(formatDiff(state), x + 14, y + 60);
	}

	ctx.strokeStyle = "rgba(255, 255, 255, 0.08)";
	ctx.lineWidth = 1;
	ctx.beginPath();
	ctx.moveTo(paneW + gutter / 2, 84);
	ctx.lineTo(paneW + gutter / 2, height);
	ctx.stroke();
	ctx.restore();
}

function roundRect(
	ctx: CanvasRenderingContext2D,
	x: number,
	y: number,
	w: number,
	h: number,
	r: number,
): void {
	ctx.beginPath();
	ctx.moveTo(x + r, y);
	ctx.arcTo(x + w, y, x + w, y + h, r);
	ctx.arcTo(x + w, y + h, x, y + h, r);
	ctx.arcTo(x, y + h, x, y, r);
	ctx.arcTo(x, y, x + w, y, r);
	ctx.closePath();
}

function formatHex(value: number): string {
	return `0x${(value >>> 0).toString(16).padStart(8, "0").toUpperCase()}`;
}

function formatDiff(state: LockstepState): string {
	const item = state.localizedDiff;
	if (item.index >= 0) {
		return `body ${item.index} ${item.field}: ${formatScalar(item.a)} vs ${formatScalar(item.b)}`;
	}
	const first = state.diff?.first;
	return first ? `${first.kind} ${first.field}: ${formatScalar(first.a)} vs ${formatScalar(first.b)}` : "diffWorldState: no localized field yet";
}

function formatScalar(value: number | boolean | string | null): string {
	if (typeof value === "number") return Number.isInteger(value) ? String(value) : value.toFixed(4);
	return String(value);
}

function statusText(state: LockstepState): string {
	return state.checksumA === state.checksumB
		? `In sync at tick ${state.tick}: ${formatHex(state.checksumA)}`
		: `Desync detected at tick ${state.desyncTick ?? state.tick}: ${formatHex(state.checksumA)} != ${formatHex(state.checksumB)}`;
}

function localizeBodyDiff(a: Client, b: Client, out: LocalizedDiff): void {
	out.index = -1;
	const count = Math.min(a.dynamicBodies.length, b.dynamicBodies.length);
	for (let i = 0; i < count; i++) {
		const bodyA = a.dynamicBodies[i]!;
		const bodyB = b.dynamicBodies[i]!;
		if (setDiff(out, i, "position.x", bodyA.position.x, bodyB.position.x)) return;
		if (setDiff(out, i, "position.y", bodyA.position.y, bodyB.position.y)) return;
		if (setDiff(out, i, "angle", bodyA.angle, bodyB.angle)) return;
		if (setDiff(out, i, "velocity.x", bodyA.velocity.x, bodyB.velocity.x)) return;
		if (setDiff(out, i, "velocity.y", bodyA.velocity.y, bodyB.velocity.y)) return;
		if (setDiff(out, i, "angularVelocity", bodyA.angularVelocity, bodyB.angularVelocity)) return;
		if (setDiff(out, i, "sleeping", bodyA.isSleeping, bodyB.isSleeping)) return;
	}
}

function setDiff(
	out: LocalizedDiff,
	index: number,
	field: string,
	a: number | boolean,
	b: number | boolean,
): boolean {
	if (a === b) return false;
	out.index = index;
	out.field = field;
	out.a = a;
	out.b = b;
	return true;
}